import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORE_KEY = 'collections_scroll_positions';
const MAX_ENTRIES = 50;

function readStore() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    const entries = Object.entries(store);
    const trimmed = entries.length > MAX_ENTRIES
      ? Object.fromEntries(entries.slice(-MAX_ENTRIES))
      : store;
    sessionStorage.setItem(STORE_KEY, JSON.stringify(trimmed));
  } catch {
    // Ignore quota/serialization errors — scroll restoration is a nicety,
    // never worth breaking navigation over.
  }
}

/**
 * Restores window scroll position when the user returns to a page via
 * browser back/forward (COL-87).
 *
 * The app uses a plain <BrowserRouter>/<Routes> tree (not a data router), so
 * react-router's own <ScrollRestoration> — which requires RouterProvider —
 * isn't available here. This hook is the library-first-adjacent substitute:
 * built entirely on react-router's own useLocation/useNavigationType
 * primitives, no hand-rolled history listening.
 *
 * Keyed by `location.key` (a fresh id react-router assigns to every history
 * entry, push or replace) rather than pathname+search, so two visits to the
 * same URL keep independent scroll positions and a `replace`d filter change
 * doesn't clobber the scroll saved for the entry a user might still return
 * to via forward.
 */
export function useScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const restoredKeyRef = useRef(null);

  // Save scroll position for the entry we're currently on whenever we're
  // about to leave it (route change, param replace, or unmount).
  useEffect(() => {
    const key = location.key;
    return () => {
      const store = readStore();
      store[key] = window.scrollY;
      writeStore(store);
    };
  }, [location.key]);

  // Restore only on a genuine back/forward (POP) — a fresh PUSH/REPLACE
  // (e.g. selecting a new category) should start at the top, not inherit
  // wherever the user happened to have scrolled to previously.
  useEffect(() => {
    if (navigationType !== 'POP') return;
    if (restoredKeyRef.current === location.key) return;
    restoredKeyRef.current = location.key;

    const store = readStore();
    const y = store[location.key];
    if (typeof y === 'number') {
      // Wait a frame so the grid has painted before scrolling to it.
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [navigationType, location.key]);
}
